import { createContext, type ReactNode, useContext, useReducer, useState } from 'react';
import { getSafeMediaUrl } from '@/utils/media-url';
import { canParseAsUrl } from '@/utils/parse-url';

type MediaState = {
  playingMedia: string | null;
  playbackTimes: Record<string, number> | null;
};

type MediaActions = {
  setPlayingMedia: (url: string) => void;
  clearPlayingMedia: () => void;
  updatePlaybackTime: (url: string, time: number) => void;
  clearAllMediaState: () => void;
  resetMediaState: (state: MediaState) => void;
};
const initialMediaState = { playingMedia: null, playbackTimes: null };

export const MediaStateContext = createContext<MediaState>(initialMediaState);
export const MediaActionsContext = createContext<MediaActions>({
  setPlayingMedia: () => {},
  clearPlayingMedia: () => {},
  updatePlaybackTime: () => {},
  clearAllMediaState: () => {},
  resetMediaState: () => {},
});

export const MediaStateProvider = ({ children }: { children: ReactNode }) => {
  const [mediaState, setMediaState] = useState<MediaState>(initialMediaState);

  const setPlayingMedia = (url: string) => {
    if (!canParseAsUrl(url)) return;
    setMediaState((prev) => ({ ...prev, playingMedia: getSafeMediaUrl(url) }));
  };

  const clearPlayingMedia = () => {
    setMediaState((prev) => ({ ...prev, playingMedia: null }));
  };

  const clearAllMediaState = () => {
    setMediaState(initialMediaState);
  };

  const resetMediaState = (state: MediaState) => {
    setMediaState(state);
  };

  const updatePlaybackTime = (url: string, time: number) => {
    if (!canParseAsUrl(url)) return;
    setMediaState((prev) => ({ ...prev, playbackTimes: { ...prev.playbackTimes, [getSafeMediaUrl(url)]: time } }));
  };

  return (
    <MediaStateContext.Provider value={mediaState}>
      <MediaActionsContext.Provider
        value={{ setPlayingMedia, clearPlayingMedia, clearAllMediaState, resetMediaState, updatePlaybackTime }}>
        {children}
      </MediaActionsContext.Provider>
    </MediaStateContext.Provider>
  );
};

export const useMediaState = () => {
  const mediaState = useContext(MediaStateContext);
  const actions = useContext(MediaActionsContext);

  return { ...actions, ...mediaState };
};
